/**
 * TradeIdeasPage — AI-generated actionable trade ideas.
 *
 * Combines signal convergence, smart money alignment, PhD agent consensus,
 * and technical patterns into scored trade ideas ranked by conviction.
 */
import { useEffect, useState, useCallback, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  RefreshCw, Zap, TrendingUp, TrendingDown, Minus,
  Shield, Brain, Activity, Target, AlertTriangle, CheckCircle, XCircle,
  HelpCircle, Info, BookOpen, Check as CheckIcon, FlaskConical,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import WatchButton from '@/components/ui/WatchButton'
import RegimeBackdrop from '@/components/ui/RegimeBackdrop'
import { PortfolioFitBadge } from '@/components/ui/PortfolioFitBadge'
import type { PortfolioFit } from '@/lib/types'
import { ccySymbol, fmtMoney, fmtNum, fmtPct, fmtPctSigned, fmtSigned } from '@/lib/format'

// ── Native-currency setup prices ────────────────────────────────────────────
// Setup levels arrive unit-less from the backend. A unit-less 452.3 is
// dangerous: US tickers quote in dollars while LSE lines quote in GBX pence.
// Label the currency explicitly from the listing suffix.
function setupCurrency(ticker: string): string | null {
  const t = (ticker ?? '').toUpperCase()
  if (t.endsWith('.L') || t.endsWith('.LN')) return 'GBX'   // LSE — pence
  if (!t.includes('.')) return 'USD'                         // plain US symbols
  return null                                                // unknown listing
}

/** Format a setup price with its native currency symbol (e.g. $395.60, 452.30p). */
export function fmtSetupPrice(n: number | null | undefined, ticker: string): string {
  const ccy = setupCurrency(ticker)
  if (ccy === 'GBX') return `${fmtNum(n)}p`                  // UK pence convention
  if (ccy && ccySymbol(ccy)) return fmtMoney(n, ccy)
  return fmtNum(n)                                           // don't guess a symbol
}

// ── Paper Lab prefill URL ───────────────────────────────────────────────────
// Contract: /app/paper-lab?ticket=SYM&side=buy|sell&entry=<n>&stop=<n>&tp=<n>
// opens the Paper Lab order ticket prefilled. All params optional except
// ticket — missing/non-finite numerics are simply omitted (null-safe).
export function paperLabPractiseUrl(
  ticker: string,
  direction?: string | null,
  entry?: number | null,
  stop?: number | null,
  tp?: number | null,
): string {
  const params = new URLSearchParams()
  params.set('ticket', (ticker ?? '').toUpperCase())
  params.set('side', String(direction ?? '').toUpperCase() === 'SHORT' ? 'sell' : 'buy')
  if (entry != null && Number.isFinite(entry)) params.set('entry', String(entry))
  if (stop != null && Number.isFinite(stop)) params.set('stop', String(stop))
  if (tp != null && Number.isFinite(tp)) params.set('tp', String(tp))
  return `/app/paper-lab?${params.toString()}`
}

// Collapsible per-card maths drawer — quant internals (sigma, Kelly fraction,
// raw score strings, sentiment decimals, regime scalars) live here so the
// visible layer stays plain-English. Collapsed by default.
function ShowMaths({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="pt-2 border-t border-border/50">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-[10px] text-text-muted hover:text-text-primary transition"
        aria-expanded={open}
      >
        <span className={clsx('inline-block transition-transform', open && 'rotate-90')}>▸</span>
        {open ? 'Hide the maths' : 'Show the maths'}
      </button>
      {open && <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">{children}</div>}
    </div>
  )
}

function InfoTip({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center cursor-help" title={text}>
      <HelpCircle size={11} className="text-text-muted opacity-50 hover:opacity-100 transition" />
    </span>
  )
}

// Collapsible explainer of how an "idea" gets scored and what the user
// should actually do with it. Sits under the header, closed by default.
function HowIdeasWork() {
  const [open, setOpen] = useState(false)
  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-surface-hover/40 transition"
      >
        <div className="flex items-center gap-2">
          <Info size={15} className="text-blue-light" />
          <span className="text-sm font-semibold text-text-primary">How trade ideas are scored</span>
          <span className="text-[10px] text-text-muted">— click for detail</span>
        </div>
        <span className={clsx('text-text-muted text-xs transition', open && 'rotate-90')}>▸</span>
      </button>
      {open && (
        <div className="border-t border-border px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-5 text-xs">
          <div>
            <div className="text-[10px] uppercase text-text-muted mb-1.5 font-semibold tracking-wider">
              Candidate pool
            </div>
            <p className="text-text-secondary leading-relaxed">
              Any ticker that has <span className="text-text-primary">2+ independent sources</span> firing in the last
              24 hours with <span className="text-text-primary">|sentiment| &gt; 0.1</span>. Noise-only chatter is
              filtered out before scoring begins.
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase text-text-muted mb-1.5 font-semibold tracking-wider">
              Conviction formula
            </div>
            <p className="text-text-secondary leading-relaxed font-mono text-[11px]">
              conv = (sources × 15) + (|sentiment| × 30) + (|smart-money sentiment| × 20) +
              (#technical patterns × 10), capped at 100.
            </p>
            <p className="text-text-secondary mt-2">
              Smart money = SEC/FCA filings, insider buys, 13F, analyst consensus, options flow, PhD agents.
              Chips are coloured by <span className="text-emerald-light">A</span>/<span className="text-blue-light">B</span>/
              <span className="text-amber-light">C</span>/<span className="text-rose-light">D</span> empirical grade.
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase text-text-muted mb-1.5 font-semibold tracking-wider">
              Suggested size
            </div>
            <p className="text-text-secondary leading-relaxed">
              Half-Kelly sized against your real deployable cash: <span className="font-mono">(conviction × |sentiment|) × 0.5</span>,
              capped at 10% of pot. Direction is LONG when sentiment &gt; 0.1, SHORT when &lt; −0.1, else HOLD.
            </p>
            <p className="text-text-secondary mt-2">
              These are ideas, not orders — execute via the rebalance queue after human review.
            </p>
          </div>
          <div className="md:col-span-3 pt-3 border-t border-border/50">
            <div className="text-[10px] uppercase text-text-muted mb-1 font-semibold tracking-wider">
              Cadence
            </div>
            <p className="text-text-secondary">
              Signals refresh every 15 min during market hours · this page auto-refreshes every 2 minutes ·
              hit Refresh to force a recompute now.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

interface TradeIdea {
  ticker: string
  direction: string
  /** Portfolio-resolved verb: 'BUY-intent LONG' becomes HOLD/TRIM when the
   *  position is already full; equals direction otherwise. Optional — old
   *  payloads without it must render exactly as before. */
  resolved_action?: string
  conviction: number
  avg_sentiment: number
  source_count: number
  total_signals: number
  sources: string[]
  smart_money: { aligned: boolean | null; sentiment: number; signal_count: number } | null
  technical_patterns: { pattern: string; direction: string; summary: string }[]
  avg_confidence: number
  sigma?: number
  is_megacap?: boolean
  tradeable?: boolean
  tradeability_note?: string | null
  proxy?: {
    adr?: string | null
    adr_note?: string
    etf?: string | null
    alt_etfs?: string[]
    rationale?: string
    region?: 'CN' | 'KR' | 'TW'
  } | null
  sigma_bonus?: number
  already_held?: boolean
  top_reasons?: { source: string | null; summary: string }[]
  backtest_stats?: {
    source: string
    grade?: string
    win_rate_5d?: number
    win_rate_20d?: number
    avg_return_5d?: number
    avg_return_20d?: number
    trades?: number
  } | null
  position_sizing?: {
    kelly_fraction: number
    kelly_fraction_raw?: number
    regime_scalar?: number
    regime_score?: number
    regime_label?: string
    suggested_gbp: number
    /** Original pre-constraint size, preserved when suggested_gbp is zeroed
     *  for HOLD/TRIM-resolved ideas. */
    suggested_gbp_unconstrained?: number
    pot_size_gbp: number
    pot_is_real?: boolean
    risk_note: string
  }
  setup?: {
    entry: number
    stop: number
    target: number
    rr_ratio: number
    atr_abs: number
    atr_pct: number
  } | null
  age_hours?: number | null
  score_breakdown?: {
    sources?: number
    sentiment?: number
    smart_money?: number
    technical?: number
    sigma?: number
    regime?: number
    /** −15 damping applied when earnings land within the setup window. */
    earnings_risk?: number
  }
  regime_flag?: 'aligned' | 'neutral' | 'fighting'
  earnings_warning?: {
    days: number
    date: string
    severity: 'high' | 'medium'
  } | null
  portfolio_fit?: PortfolioFit | null
  position_note?: string | null
}

function directionIcon(d: string) {
  if (d === 'LONG') return <TrendingUp size={16} className="text-emerald-light" />
  if (d === 'SHORT') return <TrendingDown size={16} className="text-rose-light" />
  return <Minus size={16} className="text-text-muted" />
}

function convictionColor(c: number) {
  if (c >= 70) return 'text-emerald-light'
  if (c >= 50) return 'text-amber-light'
  return 'text-text-muted'
}

function convictionBg(c: number) {
  if (c >= 70) return 'bg-emerald/10 border-emerald/20'
  if (c >= 50) return 'bg-amber/10 border-amber/20'
  return 'bg-surface-card border-border'
}

// Lazy-loaded inline pattern backtest — clicking asks the backend
// 'when these sources fired together on any ticker in last 90d with the
// same direction, what happened 5 days later?' One-click edge check.
function PatternBacktestBadge({ sources, direction }: { sources: string[]; direction: string }) {
  const [state, setState] = useState<
    | { phase: 'idle' }
    | { phase: 'loading' }
    | { phase: 'loaded'; n: number; winRate: number; avgPct: number; medianPct: number }
    | { phase: 'error' }
  >({ phase: 'idle' })

  const run = async () => {
    if (sources.length === 0) return
    setState({ phase: 'loading' })
    try {
      const res = await api.get('/api/pattern-backtest', {
        params: {
          sources: sources.slice(0, 6).join(','),
          direction: direction === 'SHORT' ? 'SHORT' : 'LONG',
          days_ahead: 5,
          lookback_days: 90,
          min_source_match: Math.min(2, sources.length),
        },
      })
      const d = res.data
      // Soft errors arrive as 200 + {"n_samples": 0, "error": …} — that must
      // read as a failure, never as a genuine "no historical pattern".
      if (typeof d?.error === 'string' && d.error) {
        setState({ phase: 'error' })
        return
      }
      if (d?.n_samples > 0) {
        setState({
          phase: 'loaded',
          n: d.n_samples,
          winRate: d.win_rate_pct,
          avgPct: d.avg_return_pct,
          medianPct: d.median_return_pct,
        })
      } else {
        setState({ phase: 'loaded', n: 0, winRate: 0, avgPct: 0, medianPct: 0 })
      }
    } catch {
      setState({ phase: 'error' })
    }
  }

  if (state.phase === 'idle') {
    return (
      <button
        type="button"
        onClick={run}
        className="underline decoration-dotted hover:text-blue-light"
        title="Historic hit-rate for tickers where this source-mix fired"
      >
        Backtest this pattern →
      </button>
    )
  }
  if (state.phase === 'loading') {
    return <span className="text-text-muted">Backtesting…</span>
  }
  if (state.phase === 'error') {
    return <span className="text-rose-light">Backtest failed</span>
  }
  if (state.n === 0) {
    return (
      <span className="text-text-muted" title="No historical hits matched this source-mix + direction in the last 90d">
        No historical pattern
      </span>
    )
  }
  return (
    <span
      className={
        state.winRate >= 60 && state.avgPct > 0 ? 'text-emerald-light'
        : state.winRate <= 40 || state.avgPct < 0 ? 'text-rose-light'
        : 'text-amber-light'
      }
      title={`Pattern fired ${state.n} times. Win rate ${fmtPct(state.winRate, 0)} · avg ${fmtPct(state.avgPct, 1)} · median ${fmtPct(state.medianPct, 1)} over 5 days.`}
    >
      📊 {state.n}× · {fmtPct(state.winRate, 0)} · {fmtPctSigned(state.avgPct, 1)}
    </span>
  )
}

export default function TradeIdeasPage() {
  const [ideas, setIdeas] = useState<TradeIdea[]>([])
  const [taxNote, setTaxNote] = useState<any>(null)
  const [sourceGrades, setSourceGrades] = useState<Record<string, string>>({})
  const [trendByTicker, setTrendByTicker] = useState<Record<string, { score: number; direction: string; acceleration: string }>>({})
  const [blotterTickers, setBlotterTickers] = useState<Set<string>>(new Set())
  const [hideBlotter, setHideBlotter] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    try {
      setLoading(true)
      const res = await api.get('/api/signals/trade-ideas')
      setIdeas(res.data?.ideas ?? [])
      setTaxNote(res.data?.tax_optimisation ?? null)
      // The backend fails soft with 200 + {"ideas": [], "error": …} — an
      // empty list with an error is an outage, not a quiet market.
      if (typeof res.data?.error === 'string' && res.data.error) {
        setError(`Trade ideas backend error: ${res.data.error}`)
      } else {
        setError(null)
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  // Pull source grades once — enriches chips without blocking the main load
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/signals/sources')
        if (cancelled) return
        const grades: Record<string, string> = {}
        for (const s of (res.data?.sources ?? []) as Array<{ name: string; backtest_grade?: string | null }>) {
          if (s.backtest_grade) grades[s.name] = s.backtest_grade
        }
        setSourceGrades(grades)
      } catch {
        // non-critical — chips just won't show grade
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Pull the trend radar ONCE and index by ticker — shows each idea's unified
  // trend direction without per-row fetches. Non-blocking, best-effort.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/trends', { params: { scope: 'movers', limit: 40 } })
        if (cancelled) return
        const map: Record<string, { score: number; direction: string; acceleration: string }> = {}
        for (const t of (res.data?.trends ?? []) as Array<{ ticker: string; trend_score: number; direction: string; acceleration: string }>) {
          if (t.ticker) map[t.ticker.toUpperCase()] = { score: t.trend_score, direction: t.direction, acceleration: t.acceleration }
        }
        setTrendByTicker(map)
      } catch {
        // non-critical — trend chip just won't show
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Pull the blotter's active (new/saved/acted) tickers so we can
  // visually mark + optionally filter out ideas already on the ledger.
  useEffect(() => {
    let cancelled = false
    const loadBlotter = async () => {
      try {
        const res = await api.get('/api/blotter/ideas?status=all&limit=500')
        if (cancelled) return
        const active = new Set<string>()
        for (const it of (res.data?.ideas ?? [])) {
          if (['new', 'saved', 'acted'].includes(it.status)) {
            active.add((it.ticker || '').toUpperCase())
          }
        }
        setBlotterTickers(active)
      } catch { /* silent */ }
    }
    loadBlotter()
    const iv = setInterval(loadBlotter, 120_000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [])

  useEffect(() => {
    fetch()
    const iv = setInterval(fetch, 120_000)
    return () => clearInterval(iv)
  }, [fetch])

  // Regime alignment: use the first idea's regime_score (all ideas share
  // the same regime context from the server). LONG ideas align with
  // risk-on (>= +1), SHORT ideas align with risk-off (<= -1).
  const regimeScore = ideas[0]?.position_sizing?.regime_score
  const potSize = ideas[0]?.position_sizing?.pot_size_gbp
  const potIsReal = ideas[0]?.position_sizing?.pot_is_real
  const regimeAlignment = regimeScore === undefined
    ? 'neutral' as const
    : regimeScore >= 1 ? 'risk_on' as const
    : regimeScore <= -1 ? 'risk_off' as const
    : 'neutral' as const

  const alignsWithRegime = (dir: string) =>
    regimeAlignment === 'neutral' ? true
    : regimeAlignment === 'risk_on' ? dir === 'LONG'
    : dir === 'SHORT'

  const [regimeFilter, setRegimeFilter] = useState(false)
  const visibleIdeas = ideas.filter(i => {
    if (regimeFilter && !alignsWithRegime(i.direction)) return false
    if (hideBlotter && blotterTickers.has(i.ticker.toUpperCase())) return false
    return true
  })

  const longs = visibleIdeas.filter(i => i.direction === 'LONG')
  const shorts = visibleIdeas.filter(i => i.direction === 'SHORT')

  return (
    <div className="space-y-6 p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-3">
            <Target size={24} className="text-amber-light" />
            Trade Ideas
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Multi-source convergences from the last 24h · scored 0–100 · sized cautiously against{' '}
            {potSize
              ? <>your <span className="text-text-secondary font-medium">£{Math.round(potSize).toLocaleString('en-GB')}</span> deployable cash{potIsReal && <span className="text-emerald-light/70"> ✓ real</span>}</>
              : 'your deployable cash'}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {regimeAlignment !== 'neutral' && (
            <button
              type="button"
              onClick={() => setRegimeFilter(f => !f)}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition',
                regimeFilter
                  ? (regimeAlignment === 'risk_on' ? 'bg-emerald/15 text-emerald-light border-emerald/40' : 'bg-rose/15 text-rose-light border-rose/40')
                  : 'bg-surface-card text-text-muted border-border hover:text-text-primary'
              )}
              title={`Show only ideas aligned with current regime (${regimeAlignment === 'risk_on' ? 'risk-on — longs only' : 'risk-off — shorts only'})`}
            >
              <Activity size={14} />
              {regimeFilter ? 'Regime-aligned' : 'Show all'}
              {regimeScore !== undefined && (
                <span className="font-mono text-xs opacity-70">
                  {fmtSigned(regimeScore, 1)}
                </span>
              )}
            </button>
          )}
          {blotterTickers.size > 0 && (
            <button
              type="button"
              onClick={() => setHideBlotter(v => !v)}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition',
                hideBlotter
                  ? 'bg-amber/15 text-amber-light border-amber/40'
                  : 'bg-surface-card text-text-muted border-border hover:text-text-primary'
              )}
              title={`Hide tickers already on your blotter (${blotterTickers.size} names)`}
            >
              {hideBlotter ? `Hiding ${blotterTickers.size} on blotter` : `Show all (${blotterTickers.size} on blotter)`}
            </button>
          )}
          <button
            type="button"
            onClick={fetch}
            disabled={loading}
            className="btn-secondary px-3 py-2 text-sm text-text-muted hover:text-text-primary"
          >
            <RefreshCw size={14} className={clsx(loading && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      <HowIdeasWork />

      <RegimeBackdrop variant="ideas" />

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-rose/10 border border-rose/30 text-rose-light text-sm">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
        <div className="card p-4">
          <div className="flex items-center gap-1.5">
            <p className="stat-label">Total Ideas</p>
            <InfoTip text="Tickers with 2+ independent sources agreeing in the last 24h (or one high-conviction filing source), scored 0–100 by conviction." />
          </div>
          <p className="stat-value">{ideas.length}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-1.5">
            <p className="stat-label">Long Ideas</p>
            <InfoTip text="Average sentiment > 0.1 — sources are bullish on the name." />
          </div>
          <p className="stat-value text-emerald-light">{longs.length}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-1.5">
            <p className="stat-label">Short Ideas</p>
            <InfoTip text="Average sentiment < −0.1 — sources are bearish. Shorts need extra care (margin, squeezes, spouse tax)." />
          </div>
          <p className="stat-value text-rose-light">{shorts.length}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-1.5">
            <p className="stat-label">Avg Conviction</p>
            <InfoTip text="Mean of the 0–100 conviction scores across all surfaced ideas. 70+ is a high-quality session, 50- suggests a quiet market." />
          </div>
          <p className="stat-value text-amber-light">
            {ideas.length > 0 ? (ideas.reduce((s, i) => s + (i.conviction ?? 0), 0) / ideas.length).toFixed(0) : 0}%
          </p>
        </div>
      </div>

      {/* Source breakdown — show WHERE these ideas are coming from */}
      {ideas.length > 0 && (
        <div className="card p-4">
          <div className="flex items-center gap-1.5 mb-2">
            <p className="panel-title">Ideas by feed</p>
            <InfoTip text="Counts every feed that contributed at least 1 signal to the surfaced ideas. A healthy mix means signals are cross-asset and cross-source, not coming from one noisy feed." />
          </div>
          {(() => {
            const bySource = new Map<string, number>()
            for (const idea of ideas) {
              for (const s of idea.sources ?? []) {
                bySource.set(s, (bySource.get(s) ?? 0) + 1)
              }
            }
            const rows = Array.from(bySource.entries())
              .sort((a, b) => b[1] - a[1])
              .slice(0, 12)
            return (
              <div className="flex flex-wrap gap-1.5">
                {rows.map(([src, n]) => (
                  <span
                    key={src}
                    className="text-[10px] px-2 py-1 rounded-full bg-surface-hover/60 text-text-secondary border border-border"
                  >
                    {src.replace(/_/g, ' ')} · <span className="text-text-primary font-semibold">{n}</span>
                  </span>
                ))}
              </div>
            )
          })()}
        </div>
      )}

      {/* Tax optimisation note */}
      {taxNote && (
        <div className="rounded-xl border border-blue/20 bg-blue/5 p-4 flex items-start gap-3">
          <Shield size={16} className="text-blue-light mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-text-primary font-semibold">{taxNote.recommendation}</p>
            <p className="text-xs text-text-muted mt-1">{taxNote.reason}</p>
            <p className="text-xs text-text-muted">
              Saving: {taxNote.saving_per_10k_profit} | Combined CGT allowance: {taxNote.combined_cgt_allowance}
            </p>
          </div>
        </div>
      )}

      {/* Ideas list */}
      {loading && ideas.length === 0 ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 card" />
          ))}
        </div>
      ) : ideas.length === 0 ? (
        <div className="text-center py-12 max-w-md mx-auto">
          <Brain size={28} className="text-text-muted opacity-30 mx-auto mb-3" />
          <p className="text-text-muted mb-1">No trade ideas right now</p>
          <p className="text-xs text-text-muted">
            Ideas appear when at least 2 independent sources agree on a ticker in the last 24h.
            Quiet news days and weekends produce fewer ideas. Hit Refresh after the next 15-min
            signal sweep (or run <code className="text-text-secondary">/collect</code> in the
            terminal) to force fresh data.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleIdeas.map(idea => (
            <div
              key={idea.ticker}
              className={clsx('card p-5 space-y-3', convictionBg(idea.conviction))}
            >
              {/* Action banner — prominent BUY/SELL call-to-action at top */}
              {(() => {
                const isGolden = (idea.sources ?? []).includes('golden_setup')
                // Portfolio-resolved action drives both verb and colour.
                // Fail-soft: old payloads without resolved_action fall back to
                // direction and render exactly as before.
                const action = String(idea.resolved_action ?? idea.direction ?? '').toUpperCase()
                const downgraded = action === 'HOLD' || action === 'TRIM'
                return (
                  <div className={clsx(
                    'rounded-lg px-3 py-2 flex items-center justify-between flex-wrap gap-2 text-sm font-semibold',
                    isGolden && !downgraded ? 'bg-amber/15 border-2 border-amber/60 text-amber-light' :
                    action === 'LONG' ? 'bg-emerald/15 border border-emerald/40 text-emerald-light' :
                    action === 'SHORT' ? 'bg-rose/15 border border-rose/40 text-rose-light' :
                    action === 'HOLD' ? 'bg-sky-500/10 border border-sky-500/40 text-sky-400' :
                    action === 'TRIM' ? 'bg-amber/15 border border-amber/40 text-amber-light' :
                    'bg-surface-hover border border-border text-text-secondary',
                  )}>
                <span className="uppercase tracking-wider text-xs opacity-80 flex items-center gap-1">
                  {isGolden && <span title="Multi-factor confirmed: 3+ high-quality sources + regime-aligned + tradeable">🌟</span>}
                  {isGolden ? 'Golden Setup' : 'Action'}
                </span>
                <span className="text-base">
                  {action === 'LONG' ? 'BUY'
                    : action === 'SHORT' ? 'SELL'
                    : action === 'HOLD' ? 'HOLD — don’t add'
                    : action === 'TRIM' ? 'TRIM — reduce, don’t buy'
                    : 'WATCH'}{' '}
                  <Link
                    to={`/app/stock/${encodeURIComponent(idea.ticker)}`}
                    className="font-mono underline hover:opacity-80"
                  >
                    {idea.ticker}
                  </Link>
                  {(() => {
                    const tr = trendByTicker[(idea.ticker ?? '').toUpperCase()]
                    if (!tr) return null
                    return (
                      <span
                        title={`Unified trend score ${fmtSigned(tr.score, 0)} (${tr.direction}, ${tr.acceleration})`}
                        className={clsx(
                          'text-[10px] px-1.5 py-0.5 rounded font-medium',
                          tr.direction === 'up' ? 'bg-emerald/15 text-emerald-light'
                          : tr.direction === 'down' ? 'bg-rose/15 text-rose-light'
                          : 'bg-surface-hover text-text-muted',
                        )}>
                        {tr.direction === 'up' ? '↗' : tr.direction === 'down' ? '↘' : '→'} trend {fmtSigned(tr.score, 0)}
                        {tr.acceleration === 'building' ? ' ▲' : ''}
                      </span>
                    )
                  })()}
                  {idea.setup && (
                    <>
                      {' @ '}
                      <span className="font-mono">{fmtSetupPrice(idea.setup.entry, idea.ticker)}</span>
                    </>
                  )}
                </span>
                {idea.setup && (
                  <span className="text-xs opacity-80">
                    stop {fmtSetupPrice(idea.setup.stop, idea.ticker)} · target {fmtSetupPrice(idea.setup.target, idea.ticker)}
                    {' · R/R '}{fmtNum(idea.setup.rr_ratio, 1)}
                  </span>
                )}
                {idea.position_sizing?.suggested_gbp !== undefined && (
                  <span className="text-xs opacity-80">
                    {downgraded && (idea.position_sizing.suggested_gbp ?? 0) <= 0
                      ? 'no new money'
                      : <>size £{Math.round(idea.position_sizing.suggested_gbp).toLocaleString('en-GB')}</>}
                  </span>
                )}
                {idea.backtest_stats && idea.backtest_stats.win_rate_5d != null && (
                  <span
                    className={clsx(
                      'text-[11px] px-1.5 py-0.5 rounded font-medium',
                      (idea.backtest_stats.win_rate_5d ?? 0) >= 55 ? 'bg-emerald/15 text-emerald-light'
                      : (idea.backtest_stats.win_rate_5d ?? 0) >= 45 ? 'bg-amber/15 text-amber-light'
                      : 'bg-rose/15 text-rose-light',
                    )}
                    title={`${idea.backtest_stats.source} backtest: ${idea.backtest_stats.win_rate_5d}% win 5d · ${idea.backtest_stats.win_rate_20d ?? '—'}% 20d · avg ${idea.backtest_stats.avg_return_5d ?? '—'}% (${idea.backtest_stats.trades ?? '—'} trades)`}>
                    📊 this signal wins {fmtPct(idea.backtest_stats.win_rate_5d, 0)} historically
                    {idea.backtest_stats.grade && idea.backtest_stats.grade !== 'N/A' ? ` · ${idea.backtest_stats.grade}` : ''}
                  </span>
                )}
              </div>
                )
              })()}

              {/* Header row */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  {directionIcon(idea.direction)}
                  <Link
                    to={`/app/stock/${encodeURIComponent(idea.ticker)}`}
                    className="text-lg font-bold text-blue-light hover:text-blue-light hover:underline"
                  >
                    {idea.ticker}
                  </Link>
                  <span className={clsx(
                    'text-xs font-semibold uppercase px-2 py-0.5 rounded-full border',
                    idea.direction === 'LONG' ? 'bg-emerald/20 text-emerald-light border-emerald/30' :
                    idea.direction === 'SHORT' ? 'bg-rose/20 text-rose-light border-rose/30' :
                    'bg-surface-hover text-text-secondary border-border',
                  )}>
                    {idea.direction}
                  </span>
                  <PortfolioFitBadge fit={idea.portfolio_fit} compact />
                  {idea.is_megacap && (
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded-full border bg-surface-hover text-text-muted border-border"
                      title="Megacap — universe the world already watches. Needs 3+ sigma to get a positive bonus."
                    >
                      MEGACAP
                    </span>
                  )}
                  {idea.tradeable === false && idea.proxy && (idea.proxy.adr || idea.proxy.etf) && (
                    <Link
                      to={`/app/stock/${encodeURIComponent((idea.proxy.adr || idea.proxy.etf)!)}`}
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-amber/10 text-amber-light border-amber/40 font-semibold hover:bg-amber/20 transition"
                      title={idea.proxy.rationale ?? `${idea.tradeability_note ?? ''} — proxy shown`}
                    >
                      via {idea.proxy.adr || idea.proxy.etf} →
                    </Link>
                  )}
                  {idea.tradeable === false && !(idea.proxy && (idea.proxy.adr || idea.proxy.etf)) && (
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded-full border bg-surface-hover text-text-muted border-border"
                      title={idea.tradeability_note ?? 'No UK-retail access'}
                    >
                      {idea.tradeability_note?.split(' — ')[0] ?? 'GLOBAL'}
                    </span>
                  )}
                  {idea.already_held && (
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded-full border bg-blue/15 text-blue-light border-blue/30"
                      title="Already in the portfolio"
                    >
                      HELD
                    </span>
                  )}
                  {!idea.already_held && (
                    <WatchButton ticker={idea.ticker} note="From Trade Ideas" />
                  )}
                  <KeepButton idea={idea} />
                  <Link
                    to={paperLabPractiseUrl(
                      idea.ticker,
                      idea.direction,
                      idea.setup?.entry,
                      idea.setup?.stop,
                      idea.setup?.target,
                    )}
                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-cyan/10 text-cyan-light border-cyan/30 hover:bg-cyan/20 transition"
                    title="Practise this setup in Paper Lab — order ticket opens prefilled, no real money"
                  >
                    <FlaskConical size={10} />
                    PRACTISE
                  </Link>
                </div>

                <div className="flex items-center gap-4">
                  {/* Conviction gauge */}
                  <div className="text-center">
                    <p className={clsx('text-xl font-bold', convictionColor(idea.conviction))}>
                      {fmtPct(idea.conviction, 0)}
                    </p>
                    <p className="text-[9px] text-text-muted uppercase">Conviction</p>
                  </div>
                </div>
              </div>

              {/* Position-awareness caveat — plain-English "you already own a lot of this" */}
              {idea.position_note && (
                <p className="flex items-start gap-1.5 text-[11px] text-text-muted leading-snug">
                  <AlertTriangle size={11} className="text-amber-light shrink-0 mt-0.5" />
                  <span>{idea.position_note}</span>
                </p>
              )}

              {/* Metrics row */}
              <div className="flex flex-wrap gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <Activity size={11} className="text-text-muted" />
                  <span className="text-text-muted">Sources:</span>
                  <span className="text-text-primary font-semibold">{idea.source_count}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Zap size={11} className="text-text-muted" />
                  <span className="text-text-muted">Signals:</span>
                  <span className="text-text-primary font-semibold">{idea.total_signals}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-text-muted">Mood:</span>
                  <span className={clsx(
                    'font-semibold',
                    (idea.avg_sentiment ?? 0) > 0.1 ? 'text-emerald-light' :
                    (idea.avg_sentiment ?? 0) < -0.1 ? 'text-rose-light' : 'text-text-muted',
                  )}>
                    {(idea.avg_sentiment ?? 0) > 0.1 ? 'bullish' : (idea.avg_sentiment ?? 0) < -0.1 ? 'bearish' : 'mixed'}
                  </span>
                </div>
                {/* Smart Money */}
                {idea.smart_money && (
                  <div className="flex items-center gap-1.5">
                    <Shield size={11} className="text-text-muted" />
                    <span className="text-text-muted">Smart Money:</span>
                    {idea.smart_money.aligned === true && (
                      <span className="flex items-center gap-0.5 text-emerald-light">
                        <CheckCircle size={10} /> Aligned
                      </span>
                    )}
                    {idea.smart_money.aligned === false && (
                      <span className="flex items-center gap-0.5 text-rose-light">
                        <XCircle size={10} /> Divergent
                      </span>
                    )}
                    {idea.smart_money.aligned === null && (
                      <span className="text-text-muted">No data</span>
                    )}
                  </div>
                )}
              </div>

              {/* Source chips — coloured by empirical backtest grade */}
              <div className="flex flex-wrap gap-1.5">
                {(idea.sources ?? []).slice(0, 8).map(s => {
                  const grade = sourceGrades[s]
                  const cls = grade === 'A'
                    ? 'bg-emerald/15 text-emerald-light border-emerald/40'
                    : grade === 'B'
                    ? 'bg-blue/15 text-blue-light border-blue/40'
                    : grade === 'C'
                    ? 'bg-amber/15 text-amber-light border-amber/40'
                    : grade === 'D'
                    ? 'bg-rose/15 text-rose-light border-rose/40'
                    : 'bg-surface-hover border-border text-text-muted'
                  return (
                    <span
                      key={s}
                      title={grade ? `Backtest grade: ${grade}` : undefined}
                      className={clsx('text-[9px] px-1.5 py-0.5 rounded border', cls)}
                    >
                      {(s ?? '').replace(/_/g, ' ')}{grade ? ` ${grade}` : ''}
                    </span>
                  )
                })}
              </div>

              {/* Why — the actual signals driving this idea, not just source names */}
              {(idea.top_reasons ?? []).length > 0 && (
                <div className="space-y-1 rounded-lg bg-surface-card/40 border border-border/20 px-3 py-2">
                  <div className="panel-title">Why</div>
                  {idea.top_reasons!.slice(0, 3).map((rsn, i) => (
                    <div key={i} className="flex gap-2 text-xs">
                      {rsn.source && <span className="font-mono text-[10px] text-text-muted shrink-0 mt-0.5 w-28 truncate">{rsn.source.replace(/_/g, ' ')}</span>}
                      <span className="text-text-secondary">{rsn.summary}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Technical patterns */}
              {(idea.technical_patterns ?? []).length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Brain size={11} className="text-purple-light" />
                  {idea.technical_patterns.map((tp, i) => (
                    <span
                      key={i}
                      className={clsx(
                        'text-[9px] px-1.5 py-0.5 rounded border',
                        tp.direction === 'bullish' ? 'bg-emerald/10 text-emerald-light border-emerald/20' :
                        'bg-rose/10 text-rose-light border-rose/20',
                      )}
                    >
                      {(tp.summary ?? tp.pattern ?? '').slice(0, 40)}
                    </span>
                  ))}
                </div>
              )}

              {/* Entry / Stop / Target setup — 2:1 R/R from ATR */}
              {idea.setup && (
                <div className="flex items-center gap-4 pt-2 border-t border-border/50 text-[11px]">
                  <span className="text-text-muted">Setup:</span>
                  <span>
                    <span className="text-text-muted">Entry </span>
                    <span className="text-text-primary font-mono font-semibold">{fmtSetupPrice(idea.setup.entry, idea.ticker)}</span>
                  </span>
                  <span>
                    <span className="text-rose-light">Stop </span>
                    <span className="text-rose-light font-mono font-semibold">{fmtSetupPrice(idea.setup.stop, idea.ticker)}</span>
                  </span>
                  <span>
                    <span className="text-emerald-light">Target </span>
                    <span className="text-emerald-light font-mono font-semibold">{fmtSetupPrice(idea.setup.target, idea.ticker)}</span>
                  </span>
                  <span className="text-text-muted"
                        title="Reward-to-risk ratio — target distance vs stop distance. Volatility detail under 'Show the maths'.">
                    R/R {fmtNum(idea.setup.rr_ratio, 1)}
                  </span>
                </div>
              )}

              {/* Context row — regime/earnings flags + age (raw score string lives in the maths drawer) */}
              {(idea.score_breakdown || idea.age_hours != null) && (
                <div className="flex items-center gap-3 text-[10px] text-text-muted border-t border-border/50 pt-2">
                  {idea.regime_flag && idea.regime_flag !== 'neutral' && (
                    <span
                      className={
                        idea.regime_flag === 'aligned'
                          ? 'px-1.5 py-0.5 rounded bg-emerald/15 text-emerald-light border border-emerald/30'
                          : 'px-1.5 py-0.5 rounded bg-rose/15 text-rose-light border border-rose/30'
                      }
                      title={idea.regime_flag === 'aligned'
                        ? 'Direction aligns with composite macro regime — tailwind'
                        : 'Direction fights composite macro regime — headwind'}
                    >
                      {idea.regime_flag === 'aligned' ? '↗ with regime' : '⚠ vs regime'}
                    </span>
                  )}
                  {idea.earnings_warning && (
                    <span
                      className={
                        idea.earnings_warning.severity === 'high'
                          ? 'px-1.5 py-0.5 rounded bg-rose/15 text-rose-light border border-rose/30'
                          : 'px-1.5 py-0.5 rounded bg-amber/15 text-amber-light border border-amber/30'
                      }
                      title={`Next earnings: ${idea.earnings_warning.date} — binary gap risk. Conviction damped -15.`}
                    >
                      📣 earnings {idea.earnings_warning.days === 0
                        ? 'TODAY'
                        : idea.earnings_warning.days === 1
                          ? 'tomorrow'
                          : `in ${idea.earnings_warning.days}d`}
                    </span>
                  )}
                  {idea.age_hours != null && (
                    <span>
                      Idea age: {idea.age_hours < 1
                        ? `${Math.round(idea.age_hours * 60)}m`
                        : idea.age_hours < 24
                          ? `${idea.age_hours.toFixed(1)}h`
                          : `${(idea.age_hours / 24).toFixed(1)}d`}
                    </span>
                  )}
                  <Link
                    to={`/app/stock/${encodeURIComponent(idea.ticker)}?tab=Signals`}
                    className="underline decoration-dotted hover:text-blue-light ml-auto"
                    title="See every signal fired on this ticker in last 30d"
                  >
                    See signals →
                  </Link>
                  <PatternBacktestBadge
                    sources={idea.sources ?? []}
                    direction={idea.direction}
                  />
                </div>
              )}

              {/* Position sizing */}
              {idea.position_sizing && ((idea.position_sizing.suggested_gbp ?? 0) > 0 || (idea.position_sizing.suggested_gbp_unconstrained ?? 0) > 0) && (
                <div className="flex items-center gap-3 pt-2 border-t border-border/50 text-[10px] text-text-muted flex-wrap">
                  {(idea.position_sizing.suggested_gbp ?? 0) === 0 && (idea.position_sizing.suggested_gbp_unconstrained ?? 0) > 0 ? (
                    <span>Suggested size: <span className="text-text-muted">
                      £0 — no new money (position already full){' '}
                      <span className="line-through opacity-60">
                        £{(idea.position_sizing.suggested_gbp_unconstrained ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                      </span>
                    </span></span>
                  ) : (
                  <span>Suggested size: <span className="text-text-primary font-semibold">
                    £{(idea.position_sizing.suggested_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                  </span></span>
                  )}
                  <span className="text-text-muted/80">sized cautiously — detail under “Show the maths”</span>
                </div>
              )}

              {/* Show the maths — sigma, Kelly fraction, raw scores, decimals */}
              <ShowMaths>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] text-text-muted w-full">
                  {(idea.sigma ?? 0) >= 2 && (
                    <span
                      className={clsx(
                        'text-[10px] font-bold px-2 py-0.5 rounded-full border',
                        (idea.sigma ?? 0) >= 4 ? 'bg-rose/20 text-rose-light border-rose/40' :
                        (idea.sigma ?? 0) >= 3 ? 'bg-amber/20 text-amber-light border-amber/40' :
                        'bg-emerald/20 text-emerald-light border-emerald/40',
                      )}
                      title={`Sigma = z-score of signal activity vs 30-day baseline. Higher = more anomalous.${
                        (idea.sigma_bonus ?? 0) !== 0 ? ` Bonus applied: ${(idea.sigma_bonus ?? 0) > 0 ? '+' : ''}${(idea.sigma_bonus ?? 0).toFixed(1)}` : ''
                      }`}
                    >
                      {fmtNum(idea.sigma, 1)}σ
                    </span>
                  )}
                  <span>
                    Sentiment{' '}
                    <span className={clsx(
                      'font-mono font-semibold',
                      (idea.avg_sentiment ?? 0) > 0.1 ? 'text-emerald-light' :
                      (idea.avg_sentiment ?? 0) < -0.1 ? 'text-rose-light' : 'text-text-muted',
                    )}>
                      {fmtSigned(idea.avg_sentiment, 3)}
                    </span>
                  </span>
                  {idea.score_breakdown && (
                    <span
                      className="cursor-help"
                      title={[
                        `Sources: +${idea.score_breakdown.sources ?? 0}`,
                        `Sentiment: +${idea.score_breakdown.sentiment ?? 0}`,
                        `Smart money: +${idea.score_breakdown.smart_money ?? 0}`,
                        `Technical: +${idea.score_breakdown.technical ?? 0}`,
                        `Sigma: ${(idea.score_breakdown.sigma ?? 0) >= 0 ? '+' : ''}${idea.score_breakdown.sigma ?? 0}`,
                        `Regime: ${(idea.score_breakdown.regime ?? 0) >= 0 ? '+' : ''}${idea.score_breakdown.regime ?? 0}`,
                        ...(idea.score_breakdown.earnings_risk != null
                          ? [`Earnings risk: ${idea.score_breakdown.earnings_risk}`]
                          : []),
                      ].join(' · ')}
                    >
                      Score: S{idea.score_breakdown.sources ?? 0}
                      ·Sent{idea.score_breakdown.sentiment ?? 0}
                      ·SM{idea.score_breakdown.smart_money ?? 0}
                      ·T{idea.score_breakdown.technical ?? 0}
                      ·σ{idea.score_breakdown.sigma ?? 0}
                      ·R{(idea.score_breakdown.regime ?? 0) >= 0 ? '+' : ''}{idea.score_breakdown.regime ?? 0}
                      {idea.score_breakdown.earnings_risk != null && <>·E{idea.score_breakdown.earnings_risk}</>}
                    </span>
                  )}
                  {idea.position_sizing && (
                    <span title="Position sized at half-Kelly — half the mathematically optimal bet, for safety">
                      Kelly (half): {fmtPct((idea.position_sizing.kelly_fraction ?? NaN) * 100, 1)}
                    </span>
                  )}
                  {idea.position_sizing?.regime_score !== undefined && idea.position_sizing?.regime_scalar !== undefined && (
                    <span
                      className={
                        (idea.position_sizing.regime_scalar ?? 1) > 1 ? 'text-emerald-light'
                        : (idea.position_sizing.regime_scalar ?? 1) < 1 ? 'text-rose-light'
                        : 'text-text-muted'
                      }
                      title="Composite regime score scales Kelly: breakout 1.3× · risk-on 1.15× · neutral 1.0× · risk-off 0.75× · panic 0.5×"
                    >
                      Regime scalar {(idea.position_sizing.regime_score ?? 0) >= 0 ? '+' : ''}
                      {fmtNum(idea.position_sizing.regime_score, 1)}
                      {' · '}{((idea.position_sizing.regime_scalar ?? 1)).toFixed(2)}×
                    </span>
                  )}
                  {idea.setup && (
                    <span title="ATR = average true range proxy (2× stdev of recent returns) — stops sit 1.5× ATR from entry, targets 3×">
                      ATR {fmtPct(idea.setup.atr_pct, 2)}
                    </span>
                  )}
                  {idea.position_sizing?.risk_note && (
                    <span className="text-text-muted/60">{idea.position_sizing.risk_note}</span>
                  )}
                </div>
              </ShowMaths>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── KeepButton: save current trade idea to the persistent blotter ──────────
function KeepButton({ idea }: { idea: TradeIdea }) {
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const handleSave = async () => {
    if (saved || busy) return
    setBusy(true)
    setErr(null)
    try {
      await api.post('/api/blotter/ideas', {
        ticker: idea.ticker,
        direction: idea.direction,
        conviction: idea.conviction,
        entry_price: idea.setup?.entry,
        stop_price: idea.setup?.stop,
        target_price: idea.setup?.target,
        rr_ratio: idea.setup?.rr_ratio,
        atr_pct: idea.setup?.atr_pct,
        suggested_gbp: idea.position_sizing?.suggested_gbp,
        regime_score: idea.position_sizing?.regime_score,
        regime_label: idea.position_sizing?.regime_label,
        sources: idea.sources ?? [],
        source_count: idea.source_count,
        avg_sentiment: idea.avg_sentiment,
        rationale: idea.technical_patterns?.[0]?.summary ?? null,
      })
      setSaved(true)
    } catch (e: any) {
      // Surface the failure inline — a silently-unsaved idea is worse than
      // an ugly message. 422s carry structured detail; keep it short.
      const detail = e?.response?.data?.detail
      const msg = typeof detail === 'string' && detail
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
          ? String(detail[0].msg)
          : (e?.message ?? 'Save failed')
      setErr(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={handleSave}
        disabled={busy || saved}
        className={clsx(
          'flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border transition',
          saved
            ? 'bg-emerald/15 text-emerald-light border-emerald/30'
            : err
              ? 'bg-rose/10 text-rose-light border-rose/30 hover:bg-rose/20'
              : 'bg-amber/10 text-amber-light border-amber/30 hover:bg-amber/20',
        )}
        title={saved ? 'Kept in blotter' : err ? `Keep failed: ${err} — click to retry` : 'Keep this idea in the blotter'}
      >
        {saved ? <CheckIcon size={10} /> : <BookOpen size={10} />}
        {saved ? 'KEPT' : err ? 'RETRY KEEP' : 'KEEP'}
      </button>
      {err && (
        <span className="text-[10px] text-rose-light max-w-[180px] truncate" title={err}>
          {err}
        </span>
      )}
    </span>
  )
}
